import { formatCurrencyCompact } from '../cfoFormat'
import { chipLabelFor } from './ceoCadenceModel'
import type {
  CeoBasisKind,
  CeoCadenceItem,
  CeoCardVisual,
  CeoCockpitResponse,
  CeoDecisionEvidence,
  CeoEvidenceUnit,
  CeoIncident,
} from './ceoCockpitTypes'

type AnalysisDriver = {
  id: string
  label: string
  value: string
  rawValue: number
  direction: 'flat'
  category: string
}

export type CeoIncidentAnalysisRequest = {
  prompt: string
  selectedMetricId: string | null
  workspaceContext: Record<string, unknown>
}

const METRIC_BY_FAMILY: Partial<Record<CeoCadenceItem['family'], string>> = {
  sales: 'sales',
  collections: 'accounts_receivable',
  cash: 'cash',
  margin: 'gross_profit',
  labor: 'labor',
  retention: 'net_revenue_retention',
  unit_economics: 'customer_payback',
  supply: 'inventory',
}

const safeNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

const formatValue = (value: number, unit: CeoEvidenceUnit | 'hours' = 'usd'): string => {
  if (unit === 'usd') return formatCurrencyCompact(value)
  if (unit === 'pct') return `${(Math.abs(value) <= 1 ? value * 100 : value).toFixed(1)}%`
  if (unit === 'months') return `${value.toFixed(1)} months`
  if (unit === 'days') return `${Math.round(value)} days`
  if (unit === 'hours') return `${value.toFixed(value % 1 ? 1 : 0)} hours`
  return value.toLocaleString('en-US', { maximumFractionDigits: 1 })
}

const driver = (
  id: string,
  label: string,
  rawValue: number | null | undefined,
  value: string,
  category: string,
): AnalysisDriver | null => {
  if (rawValue == null || !Number.isFinite(rawValue) || !label.trim() || !value.trim()) return null
  return { id, label, rawValue, value, direction: 'flat', category }
}

const evidenceDrivers = (incident?: CeoIncident | null): AnalysisDriver[] =>
  (incident?.signals ?? [])
    .flatMap((signal) => signal.evidence)
    .map((evidence: CeoDecisionEvidence, index) => (
      driver(
        evidence.metric_ref || `evidence-${index}`,
        evidence.label,
        evidence.value,
        evidence.display,
        'decision_evidence',
      )
    ))
    .filter((entry): entry is AnalysisDriver => entry != null)

const lastFinite = (values: Array<number | null | undefined>): number | null => {
  for (let index = values.length - 1; index >= 0; index -= 1) {
    const value = safeNumber(values[index])
    if (value != null) return value
  }
  return null
}

const paceDrivers = (
  visual: Extract<CeoCardVisual, { kind: 'pace_curve' }>,
  payload: CeoCockpitResponse,
  basis: CeoBasisKind,
): AnalysisDriver[] => {
  const curve = payload.pace_curves.find(
    (candidate) => candidate.metric_id === visual.metric_id && candidate.period === visual.period,
  )
  if (!curve) return []
  const actualIndex = curve.days.reduce(
    (last, day, index) => (!day.is_future && safeNumber(day.actual_cum) != null ? index : last),
    -1,
  )
  const actual = actualIndex >= 0 ? safeNumber(curve.days[actualIndex]?.actual_cum) : null
  const basisKey = basis === 'forecast' ? 'forecast_cum' : basis === 'prior_year' || basis === 'prior_period' ? 'prior_year_cum' : 'plan_cum'
  const expected = actualIndex >= 0 ? safeNumber(curve.days[actualIndex]?.[basisKey]) : null
  const priorYear = actualIndex >= 0 ? safeNumber(curve.days[actualIndex]?.prior_year_cum) : null
  const projection = safeNumber(curve.projection?.end)
  const basisLabel = basis === 'forecast' ? 'Forecast expected by now' : basis === 'prior_year' || basis === 'prior_period' ? 'Prior-year pace by now' : 'Plan expected by now'
  return [
    driver('actual-to-date', 'Actual so far', actual, actual == null ? '' : formatCurrencyCompact(actual), 'pace_fact'),
    driver('expected-to-date', basisLabel, expected, expected == null ? '' : formatCurrencyCompact(expected), 'pace_fact'),
    driver('gap-to-basis', `Gap to ${basis === 'forecast' ? 'forecast' : basis === 'plan' ? 'plan' : 'prior year'}`, actual != null && expected != null ? actual - expected : null, actual != null && expected != null ? formatCurrencyCompact(actual - expected) : '', 'pace_fact'),
    driver('projection', 'Projected month end', projection, projection == null ? '' : formatCurrencyCompact(projection), 'pace_fact'),
    driver('prior-year-to-date', 'Prior year by now', priorYear, priorYear == null ? '' : formatCurrencyCompact(priorYear), 'pace_fact'),
  ].filter((entry): entry is AnalysisDriver => entry != null)
}

const visualDrivers = (
  visual: CeoCardVisual | null | undefined,
  payload: CeoCockpitResponse,
  basis: CeoBasisKind,
): AnalysisDriver[] => {
  if (!visual) return []
  switch (visual.kind) {
    case 'aging_stack':
      return visual.rows
        .map((row, index) => {
          const pastDue = row.overdue + row.over_60
          return driver(`customer-${index}`, row.label, pastDue, formatCurrencyCompact(pastDue), 'customer')
        })
        .filter((entry): entry is AnalysisDriver => entry != null)
    case 'pace_curve':
      return paceDrivers(visual, payload, basis)
    case 'cover_bars':
      return visual.rows
        .map((row, index) => driver(
          `item-${index}`,
          row.label,
          row.out_of_stock ? 0 : safeNumber(row.days_of_supply),
          row.out_of_stock ? 'out of stock' : row.days_of_supply == null ? '' : formatValue(row.days_of_supply, 'days'),
          'inventory_item',
        ))
        .filter((entry): entry is AnalysisDriver => entry != null)
    case 'target_bullets':
      return visual.rows
        .map((row, index) => driver(`target-${index}`, row.label, row.value, formatValue(row.value, visual.unit), 'target_fact'))
        .filter((entry): entry is AnalysisDriver => entry != null)
    case 'weeks_bars':
      return visual.weeks
        .map((week, index) => driver(`week-${index}`, week.label, week.value, formatValue(week.value, visual.unit ?? 'usd'), 'weekly_fact'))
        .filter((entry): entry is AnalysisDriver => entry != null)
    case 'margin_trend':
      return visual.months
        .map((month) => driver(month.period, month.label, month.value, formatValue(month.value, 'pct'), 'monthly_fact'))
        .filter((entry): entry is AnalysisDriver => entry != null)
    case 'pareto':
      return visual.rows
        .map((row, index) => driver(`share-${index}`, row.label, row.share, formatValue(row.share, 'pct'), 'concentration_fact'))
        .filter((entry): entry is AnalysisDriver => entry != null)
    case 'payback':
      return [
        driver('payback', 'Customer payback', visual.months, formatValue(visual.months, 'months'), 'unit_economics_fact'),
        driver('comfort', 'Comfort line', visual.comfort_months, formatValue(visual.comfort_months, 'months'), 'unit_economics_fact'),
        driver('cac', 'Customer acquisition cost', safeNumber(visual.cac), visual.cac == null ? '' : formatCurrencyCompact(visual.cac), 'unit_economics_fact'),
        driver('ltv-multiple', 'Lifetime value multiple', safeNumber(visual.ltv_multiple), visual.ltv_multiple == null ? '' : `${visual.ltv_multiple.toFixed(1)}×`, 'unit_economics_fact'),
      ].filter((entry): entry is AnalysisDriver => entry != null)
    case 'cash_position':
      return [
        driver('cash-now', 'Cash now', safeNumber(visual.now), visual.now == null ? '' : formatCurrencyCompact(visual.now), 'cash_fact'),
        driver('cash-floor', 'Operating floor', safeNumber(visual.floor), visual.floor == null ? '' : formatCurrencyCompact(visual.floor), 'cash_fact'),
        driver('cash-low', '13-week low', safeNumber(visual.low_13w), visual.low_13w == null ? '' : formatCurrencyCompact(visual.low_13w), 'cash_fact'),
        driver('cash-end', 'Projected week 13', lastFinite(visual.weeks_ahead), lastFinite(visual.weeks_ahead) == null ? '' : formatCurrencyCompact(lastFinite(visual.weeks_ahead) as number), 'cash_fact'),
      ].filter((entry): entry is AnalysisDriver => entry != null)
    case 'ranked_bars':
      return visual.rows
        .map((row, index) => driver(`rank-${index}`, row.label, row.value, formatValue(row.value, visual.unit), 'ranked_fact'))
        .filter((entry): entry is AnalysisDriver => entry != null)
    case 'cohort_grid':
      return []
  }
}

const gradeFor = (item: CeoCadenceItem): 'good' | 'watch' | 'risk' | 'neutral' => {
  if (item.kind === 'decide') return 'risk'
  if (item.kind === 'watch') return 'watch'
  if (item.kind === 'celebrate') return 'good'
  return 'neutral'
}

/**
 * Preserve the exact incident evidence when a card opens Guided analysis.
 * A plain prompt loses the selected metric, the server-populated visual rows,
 * and the incident's grouped signal evidence; the generic resolver can only
 * respond with an empty "Insight Card" shell after that information is gone.
 */
export function buildCeoIncidentAnalysisRequest({
  item,
  payload,
  incident,
  basis,
}: {
  item: CeoCadenceItem
  payload: CeoCockpitResponse
  incident?: CeoIncident | null
  basis: CeoBasisKind
}): CeoIncidentAnalysisRequest {
  const signalMetric = incident?.signals
    .flatMap((signal) => [...signal.metric_refs, ...signal.evidence.map((entry) => entry.metric_ref || '')])
    .find(Boolean)
  const selectedMetricId = signalMetric || METRIC_BY_FAMILY[item.family] || null
  const metricLabel = chipLabelFor(item)
  const drivers = visualDrivers(item.visual, payload, basis)
  const resolvedDrivers = drivers.length ? drivers : evidenceDrivers(incident)
  const grade = gradeFor(item)

  return {
    selectedMetricId,
    prompt: [
      `Show the numbers behind this CEO decision: ${item.headline}`,
      item.why,
      'Use the selected incident evidence first, explain how the displayed figures support the decision, and do not substitute a generic page summary.',
    ].filter(Boolean).join('. '),
    workspaceContext: {
      interaction: 'ceo_incident_show_numbers',
      source_component: 'ceo_incident_card',
      metric_id: selectedMetricId,
      metric_label: metricLabel,
      selection_key: item.key,
      label: metricLabel,
      value: item.exposure_value ?? null,
      display: item.exposure_label || null,
      metric_tone: grade === 'risk' ? 'bad' : grade,
      deterministic_read: [item.headline, item.why].filter(Boolean).join(' '),
      source_fingerprint: payload.status.source_fingerprint || null,
      fact_ids: item.fact_ids,
      incident_key: item.key,
      incident_family: item.family,
      incident_cadence: item.cadence,
      incident_visual_kind: item.visual?.kind || null,
      drivers: resolvedDrivers,
      instant_card_verdict: {
        grade,
        headline: item.headline,
        magnitude_line: item.exposure_label || null,
      },
      instant_card_change_explanations: resolvedDrivers.length
        ? [{
            period_kind: item.cadence,
            label: 'Numbers behind this decision',
            status: 'ready',
            current: item.exposure_label || 'Current',
            prior: '-',
            change: '-',
            headline: item.why || item.headline,
            drivers: resolvedDrivers,
            confidence: 'high',
            recommended_action: item.next_step || null,
          }]
        : [],
      instant_card_prescriptive_actions: item.next_step ? [item.next_step] : [],
    },
  }
}
